'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { signOut } from 'next-auth/react';
import { SearchX } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useCurrentUser } from '@/contexts/authentication/client';
import { publicOrigin } from '@/shared/utils/publicOrigin';

export type UnavailableKind = 'story' | 'audioStory';

const stripSlash = (u: string) => u.replace(/\/+$/, '');

function accountsUrl(): string {
  return stripSlash(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200');
}

/**
 * The accounts login deep link that comes back to `returnPath` on this portal.
 * `app=portal` is required: accounts validates `return` against that app's
 * allowed origins and drops it otherwise.
 */
export function switchAccountLoginUrl(returnPath: string): string {
  const origin = publicOrigin();
  const q = new URLSearchParams({ app: 'portal' });
  if (origin) q.set('return', `${origin}${returnPath}`);
  return `${accountsUrl()}/login?${q.toString()}`;
}

/**
 * "Not found / not yours" screen for a detail page.
 *
 * One wording for both cases on purpose — the page must not reveal that an id
 * exists in another account. What it CAN say is who the viewer is signed in as:
 * the usual cause is a link opened under the wrong account, so the way out is
 * one click — back to "My stories", or sign in as somebody else and land on the
 * same link again.
 *
 * No "retry": nothing transient happened. Real failures keep their own
 * retryable error state (see resolveDetailState).
 */
export function ContentUnavailable({ kind = 'story' }: { kind?: UnavailableKind }) {
  const t = useTranslations('contentUnavailable');
  const locale = useLocale();
  const pathname = usePathname();
  const { user } = useCurrentUser();
  const [switching, setSwitching] = useState(false);

  const who = user?.email || user?.name || null;

  const handleSwitch = async () => {
    setSwitching(true);
    const loginUrl = switchAccountLoginUrl(pathname || `/${locale}`);
    // accounts logout is POST-only: it clears the shared fleet cookie, so the
    // login screen asks for an account instead of silently re-using this one.
    try {
      await fetch(`${accountsUrl()}/logout?app=portal`, { method: 'POST', credentials: 'include' });
    } catch {
      // best effort — the portal sign-out below still drops the local session
    }
    try {
      await signOut({ redirect: false });
    } catch {
      // ignore: we navigate away regardless
    }
    window.location.assign(loginUrl);
  };

  return (
    <div
      className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4 py-12 text-center"
      data-testid="content-unavailable"
      data-kind={kind}
    >
      <SearchX className="size-12 text-muted-foreground" aria-hidden />
      <div className="max-w-md space-y-2">
        <h2 className="text-2xl font-semibold text-foreground">{t(`${kind}.title`)}</h2>
        <p className="text-muted-foreground">{t(`${kind}.description`)}</p>
        {who && (
          <p className="text-sm text-muted-foreground" data-testid="content-unavailable-who">
            {t('signedInAs', { who })}
          </p>
        )}
      </div>
      <div className="flex w-full max-w-xs flex-col gap-3 sm:max-w-md sm:flex-row sm:justify-center">
        <Button asChild data-testid="content-unavailable-my-stories">
          <Link href={`/${locale}/stories`}>{t('myStories')}</Link>
        </Button>
        <Button
          variant="outline"
          onClick={handleSwitch}
          disabled={switching}
          data-testid="content-unavailable-switch-account"
        >
          {t('switchAccount')}
        </Button>
      </div>
    </div>
  );
}
